import { HttpParams } from '@angular/common/http';
import { inject, Injectable, InjectionToken, Injector, NgZone } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { TabSyncService } from '@auth/service/tab-sync.service';
import { ApiService } from '@common/service/api.service';
import { UserQuery } from '@common/store/user.store';
import { environment } from '@environments/environment';

import {
  INCLUDE_CORE_FIELDS,
  INCLUDE_ID_RE,
  layersKey,
  flatIds,
  IncludeLayer,
  IncludeResolveError,
  IncludeRow,
  IncludeSource,
  includeMarkup,
  MAX_INCLUDE_DEPTH,
  resolveIncludes,
  rowProblem,
  unknownIds,
} from './resolve-includes';
import { widgetIncludesLibrary } from '../../../generated/widget-includes-library';

/** the whole feature's switch. Off = framed HTML exactly as before. */
export const WIDGET_INCLUDES_ENABLED = new InjectionToken<boolean>('WIDGET_INCLUDES_ENABLED', {
  providedIn: 'root',
  factory: () => environment.widgetIncludes === true,
});

/** One library entry: its hashed file in `widget-includes/`, and a package's members (a package has no file read). */
export interface LibraryEntry {
  file: string;
  sha256?: string;
  kind?: string;
  includes?: string[];
}

/**
 * The include library compiled into this build (`src/generated/widget-includes-library.ts`, written by
 * `scripts/fetch-widget-includes.mjs` before `ng build`): every adopted id, and the startup ids in load order.
 * Without a fetch (`yarn start`, tests) it is empty: no startup set, every declared include read from Core.
 */
export interface WidgetIncludesLibrary {
  includes: Record<string, LibraryEntry>;
  startup: readonly { id: string; file: string; sha256?: string }[];
}

export const WIDGET_INCLUDES_LIBRARY = new InjectionToken<WidgetIncludesLibrary>('WIDGET_INCLUDES_LIBRARY', {
  providedIn: 'root',
  factory: () => widgetIncludesLibrary,
});

/** Hard limit for the startup includes, from the first request. Frames then render without missing members. */
export const STARTUP_LIMIT_MS = 1500;
/** Declared includes: after this a widget shows its error state instead of loading for ever. */
export const DECLARED_LIMIT_MS = 10000;
/** Ids asked for within this window (or before the next animation frame) share one Core read. */
export const BATCH_WINDOW_MS = 50;
/** Core's batch-read takes at most 100 keys per request (more is a 400 `CORE_INVALID_BULK_READ`). */
export const CORE_BATCH_MAX_KEYS = 100;
/**
 * Batched reads use Core's `POST …/batch-read` (body `{keys: […]}`): no URL-size limit (a `GET ?in=…` of about 90 ids
 * passes 2,048 bytes of query string and is refused by the edge's WAF), and a per-key status, 200 or 404.
 */
export const CORE_BATCH_READ_PATH = '/api/core/config/ui_includes/batch-read';
export const STARTUP_FOLDER = 'widget-includes/';
const LIBRARY_FILE_RE = /^[a-z][a-z0-9-]{2,63}\.[0-9a-f]{16}\.json$/;

/** Declared includes: layers (roots outermost first, template, node), or a plain list = the node's own. */
export type DeclaredIncludes = readonly IncludeLayer[] | readonly string[];

/** What a frame needs: the head markup to inject, or the error naming the ids that stop this widget. */
export interface ResolvedIncludes {
  key: string;
  markup: string;
  error: string | null;
  /**
   * The error came from a read that failed or did not finish in time (not from a missing, refused, looping or too
   * deep include): the widget tries again on its next data refresh or re-render.
   */
  retryable?: boolean;
  /** For a timeout: the resolution still running in the background, so the widget can use it when it arrives. */
  later?: Promise<ResolvedIncludes>;
}

interface CoreEntry {
  row: IncludeRow | null;
  why?: string;
}

interface Waiter {
  resolve: (entry: CoreEntry) => void;
  reject: (error: unknown) => void;
}

/**
 * shared widget includes.
 *
 * - **Index:** compiled into the build ({@link WIDGET_INCLUDES_LIBRARY}); never requested at run time.
 * - **Startup set:** the files the index lists as startup, fetched at app start from the app's own origin with
 *   `fetch` (outside the API client: no auth, no interceptors), within {@link STARTUP_LIMIT_MS}. Never fatal.
 * - **Library (declared ids in the index):** the id's static file, fetched only when a widget declares it (or a
 *   library package that holds it), retried once, cached for the app session. Packages expand from the index with no
 *   request. Never preloaded. A file that still fails falls back to Core.
 * - **Every other declared id** is read from Core in one batched, cached read per page (`POST batch-read`,
 *   `show_entity=current`, up to 100 keys each), with one more read per package level still unknown. Rows and
 *   confirmed absences are cached per entity and id; failures are not cached. That cache is dropped when the user or
 *   entity changes (library rows stay: they belong to this release).
 * - The host logs each failure or warning once per page.
 */
@Injectable({ providedIn: 'root' })
export class WidgetIncludesService {
  readonly enabled = inject(WIDGET_INCLUDES_ENABLED);
  private readonly library = new Map<string, LibraryEntry>();
  private readonly startupFiles: { id: string; file: string }[] = [];
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);

  private startupIds: string[] = [];
  private readonly startupRows = new Map<string, IncludeRow>();
  private readonly startupFailures = new Map<string, string>();
  private startupPromise: Promise<void> | null = null;
  private startupSettled = false;

  /** Library rows (and packages from the index) for the app session; ids whose file failed go to Core instead. */
  private readonly libraryRows = new Map<string, IncludeRow>();
  private readonly libraryFailed = new Set<string>();
  private readonly libraryInflight = new Map<string, Promise<void>>();

  private readonly core = new Map<string, CoreEntry>();
  private readonly inflight = new Map<string, Promise<CoreEntry>>();
  private batch: { entity: string; generation: number; waiters: Map<string, Waiter[]> } | null = null;
  private readonly results = new Map<string, ResolvedIncludes>();
  private readonly resolving = new Map<string, Promise<ResolvedIncludes>>();
  private cacheOwner: string | null = null;
  /** Bumped by every clear(): a read or resolution started before a clear never writes into the cleared cache. */
  private generation = 0;
  private readonly logged = new Set<string>();

  constructor() {
    this.readIndex(inject(WIDGET_INCLUDES_LIBRARY));
    // A tenant switch in another tab: drop this tab's cache too (this tab's own switch clears in EntityService).
    if (this.enabled) {
      inject(TabSyncService, { optional: true })?.entitySwitch$?.subscribe(() => this.clear());
    }
  }

  /** Start loading the startup set (idempotent). Settles when loaded or after the limit, never rejects. */
  start(): Promise<void> {
    if (!this.enabled) {
      this.startupSettled = true;
      return Promise.resolve();
    }
    if (!this.startupPromise) {
      this.startupPromise = this.zone.runOutsideAngular(() =>
        Promise.race([this.loadStartup(), delay(STARTUP_LIMIT_MS)]).then(() => {
          this.startupSettled = true;
          for (const id of this.startupIds) {
            if (!this.startupRows.has(id) && !this.startupFailures.has(id)) this.startupFailures.set(id, 'timeout');
          }
          this.startupFailures.forEach((why, id) => this.logOnce('error', `[aiwb include] ${id}: ${why}`));
        }),
      );
    }
    return this.startupPromise;
  }

  /** The result for these declared includes if everything is already in hand; null when a read is still needed. */
  peek(given: DeclaredIncludes): ResolvedIncludes | null {
    if (!this.startupSettled) return null;
    const entity = this.entityKey();
    const memo = this.results.get(entity + '\u0000' + layersKey(given));
    if (memo) return memo;
    if (
      unknownIds(
        flatIds(given),
        (id) => this.isKnown(entity, id),
        (id) => this.row(entity, id),
      ).length
    )
      return null;
    return this.finish(entity, given, new Map());
  }

  /** Resolve these declared includes: startup set first, then the library, every other id from Core. Never rejects. */
  resolve(given: DeclaredIncludes): Promise<ResolvedIncludes> {
    const entity = this.entityKey();
    const memoKey = entity + '\u0000' + layersKey(given);
    const memo = this.results.get(memoKey);
    if (memo) return Promise.resolve(memo);
    let pending = this.resolving.get(memoKey);
    if (!pending) {
      pending = this.withLimit(given, this.doResolve(entity, given));
      this.resolving.set(memoKey, pending);
      const forget = () => this.resolving.delete(memoKey);
      pending.then(forget, forget);
    }
    return pending;
  }

  /**
   * Drop every cached Core row, absence and result (startup and library rows stay: they belong to this release). On
   * a tenant change (entity switch, in this tab or another) and when the user or entity seen at request time changes.
   */
  clear(): void {
    this.generation++;
    this.core.clear();
    this.inflight.clear(); // pending reads still answer their waiters; new asks start fresh reads
    this.results.clear();
    this.resolving.clear();
  }

  /** Console line once per page (the host logs; frames never do). */
  logOnce(level: 'warn' | 'error', line: string): void {
    if (this.logged.has(line)) return;
    this.logged.add(line);
    if (level === 'error') console.error(line);
    else console.warn(line);
  }

  // ---------------------------------------------------------------------------------------------------------------

  private async doResolve(entity: string, given: DeclaredIncludes): Promise<ResolvedIncludes> {
    await this.start();
    const generation = this.generation;
    const ids = flatIds(given);
    const failures = new Map<string, string>();
    // One read per level the depth limit allows: the given lists (level 0) and up to MAX_INCLUDE_DEPTH below them.
    for (let round = 0; round <= MAX_INCLUDE_DEPTH; round++) {
      const unknown = unknownIds(
        ids,
        (id) => this.isKnown(entity, id) || failures.has(id),
        (id) => this.row(entity, id),
      );
      if (!unknown.length) break;
      const settled = await Promise.allSettled(
        unknown.map((id) => (this.inLibrary(id) ? this.fromLibrary(entity, id) : this.requestRow(entity, id))),
      );
      settled.forEach((s, i) => {
        if (s.status === 'rejected') failures.set(unknown[i], 'Core read failed');
      });
    }
    return this.finish(entity, given, failures, generation);
  }

  private finish(
    entity: string,
    given: DeclaredIncludes,
    readFailures: Map<string, string>,
    generation = this.generation,
  ): ResolvedIncludes {
    const key = layersKey(given);
    const sources = new Map<string, IncludeSource>();
    const failures = new Map<string, string>(this.startupFailures);
    // where each row in hand came from (a startup id whose file failed shows the source that supplied it instead)
    this.startupRows.forEach((_, id) => sources.set(id, 'startup'));
    this.libraryRows.forEach((_, id) => sources.has(id) || sources.set(id, 'library'));
    this.core.forEach((entry, k) => {
      const [owner, id] = k.split('\u0000');
      if (owner !== entity) return;
      if (!sources.has(id)) sources.set(id, 'core');
      if (entry.why) failures.set(id, entry.why);
    });
    this.startupIds.forEach((id) => sources.has(id) || sources.set(id, 'startup'));
    readFailures.forEach((why, id) => failures.set(id, why));

    let result: ResolvedIncludes;
    try {
      const res = resolveIncludes(this.startupIds, given, (id) => this.row(entity, id), { sources, failures });
      res.failures.forEach((f) => this.logOnce('error', `[aiwb include] ${f.id}: ${f.error}`));
      res.warnings.forEach((w) => this.logOnce('warn', `[aiwb include] ${w}`));
      result = {
        key,
        markup: includeMarkup(res.assets, res.diagnostics),
        error: res.ok ? null : `Include not available: ${res.missing.join(', ')}`,
        // a read failed, or the cache was cleared (tenant switch) while this resolution ran
        retryable: !res.ok && (res.missing.some((id) => readFailures.has(id)) || generation !== this.generation),
      };
    } catch (e) {
      const message = e instanceof IncludeResolveError ? e.message : 'Include could not be used';
      this.logOnce('error', `[aiwb include] ${message}`);
      result = { key, markup: '', error: message };
    }
    // A failed read is never remembered: the next build (or page load) tries again.
    if (!readFailures.size && generation === this.generation) this.results.set(entity + '\u0000' + key, result);
    return result;
  }

  private withLimit(given: DeclaredIncludes, work: Promise<ResolvedIncludes>): Promise<ResolvedIncludes> {
    const ids = [...new Set(flatIds(given))];
    return this.zone.runOutsideAngular(() => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const limit = new Promise<ResolvedIncludes>((resolve) => {
        timer = setTimeout(() => {
          this.logOnce('error', `[aiwb include] ${ids.join(', ')}: timeout`);
          resolve({
            key: layersKey(given),
            markup: '',
            error: `Include not available: ${ids.join(', ')}`,
            retryable: true,
            later: work,
          });
        }, DECLARED_LIMIT_MS);
      });
      return Promise.race([work, limit]).finally(() => clearTimeout(timer));
    });
  }

  /**
   * Known = in hand or settled. A startup id that failed (timeout, failed, refused) is not known for a declared
   * reference: that falls through to the library file, then the batched Core read, instead of being silently dropped.
   */
  private isKnown(entity: string, id: string): boolean {
    return (
      (this.startupIds.includes(id) && !this.startupFailures.has(id)) ||
      this.libraryRows.has(id) ||
      this.core.has(entity + '\u0000' + id)
    );
  }

  private row(entity: string, id: string): IncludeRow | null {
    return this.startupRows.get(id) ?? this.libraryRows.get(id) ?? this.core.get(entity + '\u0000' + id)?.row ?? null;
  }

  /** Cache key: the signed-in user's entity, else the tenant domain for a guest. Changing it drops the cache. */
  private entityKey(): string {
    let user: { user_id?: string; entity?: { entity_id?: string } } | undefined;
    try {
      user = this.injector.get(UserQuery, null)?.getValue() as typeof user;
    } catch {
      user = undefined;
    }
    const entity = user?.entity?.entity_id ? 'entity:' + user.entity.entity_id : 'guest@' + location.host;
    const owner = entity + '|' + (user?.user_id ?? '');
    if (this.cacheOwner !== null && this.cacheOwner !== owner) this.clear();
    this.cacheOwner = owner;
    return entity;
  }

  // --- Core reader -------------------------------------------------------------------------------------------------

  private requestRow(entity: string, id: string): Promise<CoreEntry> {
    const k = entity + '\u0000' + id;
    const cached = this.core.get(k);
    if (cached) return Promise.resolve(cached);
    let pending = this.inflight.get(k);
    if (!pending) {
      pending = new Promise<CoreEntry>((resolve, reject) => this.enqueue(entity, id, { resolve, reject }));
      this.inflight.set(k, pending);
      const forget = () => this.inflight.delete(k);
      pending.then(forget, forget);
    }
    return pending;
  }

  private enqueue(entity: string, id: string, waiter: Waiter): void {
    if (this.batch && (this.batch.entity !== entity || this.batch.generation !== this.generation)) this.flush();
    if (!this.batch) {
      const batch = { entity, generation: this.generation, waiters: new Map<string, Waiter[]>() };
      this.batch = batch;
      this.zone.runOutsideAngular(() => {
        const go = () => {
          if (this.batch === batch) this.flush();
        };
        setTimeout(go, BATCH_WINDOW_MS);
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(go);
      });
    }
    const list = this.batch.waiters.get(id) ?? [];
    list.push(waiter);
    this.batch.waiters.set(id, list);
  }

  private flush(): void {
    const batch = this.batch;
    this.batch = null;
    if (!batch) return;
    const ids = [...batch.waiters.keys()];
    for (let i = 0; i < ids.length; i += CORE_BATCH_MAX_KEYS) {
      const chunk = ids.slice(i, i + CORE_BATCH_MAX_KEYS);
      this.readChunk(chunk).then(
        (answers) => {
          for (const id of chunk) {
            const answer = answers.get(id);
            let entry: CoreEntry | null = null; // no answer for this key: not cached, the read counts as failed
            if (answer?.status === 404) {
              entry = { row: null };
            } else if (answer?.status === 200 && answer.record) {
              const why = rowProblem(id, answer.record);
              entry = why ? { row: null, why } : { row: answer.record };
            }
            // A read that started before a clear (tenant switch) answers its waiters but never fills the new cache.
            if (entry && batch.generation === this.generation) this.core.set(batch.entity + '\u0000' + id, entry);
            batch.waiters.get(id)?.forEach((w) => (entry ? w.resolve(entry) : w.reject(new Error('no answer'))));
          }
        },
        (error) => chunk.forEach((id) => batch.waiters.get(id)?.forEach((w) => w.reject(error))),
      );
    }
  }

  /** One batch-read of up to 100 keys, through the API client (auth, interceptors, entity), retried once. */
  private async readChunk(ids: string[]): Promise<Map<string, BatchReadAnswer>> {
    const api = this.injector.get(ApiService);
    const params = new HttpParams({ fromObject: { show_entity: 'current' } });
    const once = () =>
      firstValueFrom(this.zone.run(() => api.callApi<unknown>('post', CORE_BATCH_READ_PATH, { keys: ids }, params)));
    let body: unknown;
    try {
      body = await once();
    } catch {
      body = await once();
    }
    return parseBatchRead(body);
  }

  // --- Library ---------------------------------------------------------------------------------------------------

  /** The compiled index: well-formed `aiwb-` entries only. Packages become rows here (no file is ever read for one). */
  private readIndex(index: WidgetIncludesLibrary | null | undefined): void {
    let ignored = 0;
    const wellFormed = (id: unknown, file: unknown): id is string =>
      typeof id === 'string' &&
      INCLUDE_ID_RE.test(id) &&
      id.startsWith('aiwb-') &&
      typeof file === 'string' &&
      LIBRARY_FILE_RE.test(file);
    for (const [id, entry] of Object.entries(index?.includes ?? {})) {
      const members = entry?.includes;
      const okMembers = members == null || (Array.isArray(members) && members.every((m) => typeof m === 'string'));
      if (!wellFormed(id, entry?.file) || !okMembers) {
        ignored++;
        continue;
      }
      this.library.set(id, entry);
      if (entry.kind === 'package') {
        this.libraryRows.set(id, { include_id: id, kind: 'package', includes: [...(members ?? [])] });
      }
    }
    for (const entry of index?.startup ?? []) {
      if (wellFormed(entry?.id, entry?.file) && !this.startupFiles.some((e) => e.id === entry.id)) {
        this.startupFiles.push({ id: entry.id, file: entry.file });
      } else ignored++;
    }
    this.startupIds = this.startupFiles.map((e) => e.id);
    if (ignored) this.logOnce('error', `[aiwb include] library index: ${ignored} entr(ies) ignored (not well formed)`);
  }

  private inLibrary(id: string): boolean {
    return this.library.has(id) && !this.libraryFailed.has(id);
  }

  /**
   * A declared library id: its static file (same origin, retried once), cached for the app session. When the file
   * still fails or is refused, the id is read from Core instead (for the rest of this page too).
   */
  private fromLibrary(entity: string, id: string): Promise<unknown> {
    let pending = this.libraryInflight.get(id);
    if (!pending) {
      const file = this.library.get(id)!.file;
      pending = this.zone.runOutsideAngular(async () => {
        let row: unknown;
        try {
          row = await fetchJson(STARTUP_FOLDER + file, 'default');
        } catch {
          row = await fetchJson(STARTUP_FOLDER + file, 'default');
        }
        const why = rowProblem(id, row);
        if (why) throw new Error(why);
        this.libraryRows.set(id, row as IncludeRow);
      });
      this.libraryInflight.set(id, pending);
      pending.then(
        () => this.libraryInflight.delete(id),
        (e) => {
          this.libraryInflight.delete(id);
          this.libraryFailed.add(id);
          this.logOnce('warn', `[aiwb include] ${id}: library file ${file} failed (${describe(e)}); read from Core`);
        },
      );
    }
    return pending.catch(() => this.requestRow(entity, id));
  }

  // --- Startup set -------------------------------------------------------------------------------------------------

  private async loadStartup(): Promise<void> {
    await Promise.all(
      this.startupFiles.map(async ({ id, file }) => {
        let row: unknown;
        try {
          row = await fetchJson(STARTUP_FOLDER + file, 'default');
        } catch {
          try {
            row = await fetchJson(STARTUP_FOLDER + file, 'default');
          } catch (e) {
            if (!this.startupSettled) this.startupFailures.set(id, `failed (${file}: ${describe(e)})`);
            return;
          }
        }
        if (this.startupSettled) return; // after the limit the set is fixed for this page
        const why =
          rowProblem(id, row) ??
          (!id.startsWith('aiwb-') ? 'not an aiwb- id' : (row as IncludeRow).kind === 'package' ? 'a package' : null);
        if (why) this.startupFailures.set(id, why);
        else this.startupRows.set(id, row as IncludeRow);
      }),
    );
  }
}

export interface BatchReadAnswer {
  status: number;
  record?: IncludeRow;
}

/**
 * Core's batch-read answer `{results: [{record_id, status, record}]}` → per key: 200 with the row (only the fields the
 * frontend uses), 404 missing, anything else as given (not cached). A body of another shape is a failed read.
 */
export function parseBatchRead(body: unknown): Map<string, BatchReadAnswer> {
  const results = (body as { results?: unknown } | null)?.results;
  if (!Array.isArray(results)) throw new Error('unexpected response');
  const answers = new Map<string, BatchReadAnswer>();
  for (const result of results) {
    const r = result as { record_id?: unknown; status?: unknown; record?: Record<string, unknown> } | null;
    if (!r || typeof r.record_id !== 'string') continue;
    const status = Number(r.status);
    const record = status === 200 && r.record && typeof r.record === 'object' ? pickFields(r.record) : undefined;
    answers.set(r.record_id, { status, record });
  }
  return answers;
}

/** Only the fields the frontend uses (batch-read returns whole rows). */
function pickFields(record: Record<string, unknown>): IncludeRow {
  const picked: Record<string, unknown> = {};
  for (const field of INCLUDE_CORE_FIELDS) {
    if (record[field] !== undefined) picked[field] = record[field];
  }
  return picked as unknown as IncludeRow;
}

/** Same-origin static file, outside the API client: no credentials, no auth headers, no interceptors. */
async function fetchJson(path: string, cache: RequestCache): Promise<unknown> {
  const url = new URL(path, document.baseURI).href;
  const response = await fetch(url, { credentials: 'omit', cache });
  if (!response.ok) throw new Error('HTTP ' + response.status);
  return response.json();
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
